import { motion } from 'framer-motion'
import styled, { keyframes } from 'styled-components'
import CountUp from 'react-countup'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { colors, fontWeights } from '@/styles'
import { LargeLock, TrustScoreClock, InfoCircleSmall } from '@/components/icons'
import { getModalInfo, showInfoModal } from '@/features/Application/info-modal'
import Skeleton from '@/components/skeleton'
import ScorePathSkeleton from './score-path-skeleton'

const getRotation = props => keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: ${`rotate(${props.rotation || '0deg'})`};
  }
`
const Bar = styled.div`
  display: flex;
  align-items: center;
  width: 200px;
  height: 10px;
  background: rgba(0, 0, 0, 0);
  position: absolute;
  top: 95px;
  animation: ${props => getRotation(props)} ${props => (props.duration ? props.duration : 0)}s
    linear forwards;
  animation-delay: ${({ delay }) => (delay ? delay : 0)}s;
`

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  color: ${({ disabledStyling, textColor }) => (!disabledStyling ? textColor : colors.grey72)};
  font-weight: ${fontWeights.bold};
  top: ${({ disabledStyling }) => (!disabledStyling ? 60 : 42)}px;
`

const ScoreNumber = styled.span`
  font-size: 60px;
`

const ScoreTextContainer = styled.div`
  width: 190px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  color: ${colors.grey32};
  margin-top: ${({ disabledStyling }) => (!disabledStyling ? 28 : 15)}px;
`

const ScoreEnd = styled.span`
  display: flex;
  justify-content: center;
  font-size: 14px;
  color: ${colors.grey64};
  width: 15px;
`

const Circle = styled.div`
  width: 20px;
  height: 20px;
  border: ${({ borderColor }) => (borderColor ? `4px solid ${borderColor}` : '4px solid #B8B8B8')};
  background-color: #fff;
  border-radius: 50%;
`

const Container = styled.div`
  position: relative;
  width: 200px;
  height: 100%;
  display: flex;
  justify-content: center;
`

const ScoreInfoContainer = styled.button`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.grey40};
  cursor: pointer;
  margin: 8px 0 0;
  background: none;
  border: none;
  padding: 0;
`

const ClickWrapper = styled.div`
  display: flex;
  align-items: center;
  pointer-events: none;
`

const StyledInfoCircle = styled(InfoCircleSmall)`
  margin-left: 5px;
`

const WhatsThisText = styled.span`
  text-decoration: underline;
  font-weight: ${fontWeights.medium};
  :hover {
    color: ${colors.grey24};
  }
`

const getPathDuration = (totalDuration, pathLength, totalPathLength) =>
  totalDuration * (pathLength / totalPathLength)

const getLastPathLength = (fullLength, num, denom) => fullLength * (num / denom)

const totalDuration = 2.5
const durationIncrement = totalDuration / 4
const poorTotal = durationIncrement
const averageTotal = durationIncrement * 2
const veryGoodTotal = durationIncrement * 3
const excellentTotal = durationIncrement * 4
const initialDelay = 0.5

//general path render delay to ensure path drawing does not outpace dot path
const pathRenderDelay = 0.1

const path1Length = 0.15
const path2Length = 0.089
const path3Length = 0.091
const path4Length = 0.0915

const poorScoreStart = 0
const poorScoreEnd = 59
const poorScoreRange = poorScoreEnd - poorScoreStart

const averageScoreStart = 60
const averageScoreEnd = 74
const averageScoreRange = averageScoreEnd - averageScoreStart

const veryGoodScoreStart = 75
const veryGoodScoreEnd = 89
const veryGoodScoreRange = veryGoodScoreEnd - veryGoodScoreStart

const excellentScoreStart = 90
const excellentScoreEnd = 100
const excellentScoreRange = excellentScoreEnd - excellentScoreStart

const poorArcDegreeRange = 52
const averageArcDegreeRange = 26
const averageArcDegreeStart = 66
const veryGoodArcDegreeRange = 28
const veryGoodArcDegreeStart = 107
const excellentArcDegreeRange = 30
const excellentArcDegreeStart = 149

const AnimatedTrustScore = ({ score = 0, rate = 'default', isAnalyzing, isLocked, isLoading }) => {
  const averageScoreDecimal = (score - averageScoreStart) / averageScoreRange
  const veryGoodScoreDecimal = (score - veryGoodScoreStart) / veryGoodScoreRange
  const excellentScoreDecimal = (score - excellentScoreStart) / excellentScoreRange
  const { t } = useTranslation()

  const scoreTier = {
    poor: {
      totalDuration: poorTotal,
      dotAngle: Math.floor((score / averageScoreStart) * poorArcDegreeRange),
      text: t('l.application.view.poor.text', 'Poor'),
      color: colors.red40,
      path1: {
        length: getLastPathLength(path1Length, score, averageScoreStart - 1),
        duration: poorTotal,
        delay: initialDelay + pathRenderDelay
      },
      path2: { length: 0, duration: 0 },
      path3: { length: 0, duration: 0 },
      path4: { length: 0, duration: 0 }
    },
    average: {
      totalDuration: averageTotal,
      dotAngle: Math.floor(averageScoreDecimal * averageArcDegreeRange) + averageArcDegreeStart,
      text: t('l.application.view.average.text', 'Average'),
      color: colors.yellow48,
      path1: {
        length: path1Length,
        duration: getPathDuration(
          averageTotal,
          path1Length,
          path2Length * averageScoreDecimal + path1Length
        ),
        delay: initialDelay
      },
      path2: {
        length: getLastPathLength(path2Length, score - averageScoreStart, averageScoreRange),

        duration: getPathDuration(
          averageTotal,
          path2Length * averageScoreDecimal,
          path2Length * averageScoreDecimal + path1Length
        ),
        delay:
          getPathDuration(
            averageTotal,
            path1Length,
            path2Length * averageScoreDecimal + path1Length
          ) +
          initialDelay +
          pathRenderDelay
      },
      path3: { length: 0, duration: 0 },
      path4: { length: 0, duration: 0 }
    },
    very_good: {
      totalDuration: veryGoodTotal,
      dotAngle: Math.floor(veryGoodScoreDecimal * veryGoodArcDegreeRange) + veryGoodArcDegreeStart,
      text: t('l.application.view.very_good.text', 'Very Good'),
      color: colors.veryGood,
      path1: {
        length: path1Length,
        duration:
          getPathDuration(
            veryGoodTotal,
            path1Length,
            path3Length * veryGoodScoreDecimal + path1Length + path2Length
          ) - 0.2,
        delay: initialDelay + pathRenderDelay
      },
      path2: {
        length: path2Length,
        duration: getPathDuration(
          veryGoodTotal,
          path2Length,
          path3Length * veryGoodScoreDecimal + path1Length + path2Length
        ),
        delay:
          getPathDuration(
            veryGoodTotal,
            path1Length,
            path3Length * veryGoodScoreDecimal + path1Length + path2Length
          ) + initialDelay
      },
      path3: {
        length: getLastPathLength(path3Length, score - veryGoodScoreStart, veryGoodScoreRange),
        duration: getPathDuration(
          veryGoodTotal,
          path3Length * veryGoodScoreDecimal,
          path3Length * veryGoodScoreDecimal + path1Length + path2Length
        ),
        delay:
          getPathDuration(
            veryGoodTotal,
            path1Length,
            path3Length * veryGoodScoreDecimal + path1Length + path2Length
          ) +
          getPathDuration(
            veryGoodTotal,
            path2Length,
            path3Length * veryGoodScoreDecimal + path1Length + path2Length
          ) +
          initialDelay +
          pathRenderDelay
      },
      path4: { length: 0, duration: 0 }
    },
    excellent: {
      totalDuration: excellentTotal,
      dotAngle:
        Math.floor(excellentScoreDecimal * excellentArcDegreeRange) + excellentArcDegreeStart,
      text: t('l.application.view.excellent.text', 'Excellent'),
      color: colors.green48,
      path1: {
        length: path1Length,
        // shorten the following duration by 0.2s tp prevent first path lagging too far behind dot
        duration:
          getPathDuration(
            excellentTotal,
            path1Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) - 0.2,
        delay: initialDelay + pathRenderDelay
      },
      path2: {
        length: path2Length,
        duration: getPathDuration(
          excellentTotal,
          path2Length,
          path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
        ),
        delay:
          getPathDuration(
            excellentTotal,
            path1Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path4Length
          ) + initialDelay
      },
      path3: {
        length: path3Length,
        duration: getPathDuration(
          excellentTotal,
          path3Length,
          path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
        ),
        delay:
          getPathDuration(
            excellentTotal,
            path1Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) +
          getPathDuration(
            excellentTotal,
            path2Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) +
          initialDelay
      },
      path4: {
        length: getLastPathLength(path4Length, score - excellentScoreStart, excellentScoreRange),
        duration: getPathDuration(
          excellentTotal,
          path4Length * excellentScoreDecimal,
          path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
        ),
        delay:
          getPathDuration(
            excellentTotal,
            path1Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) +
          getPathDuration(
            excellentTotal,
            path2Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) +
          getPathDuration(
            excellentTotal,
            path3Length,
            path4Length * excellentScoreDecimal + path1Length + path2Length + path3Length
          ) +
          initialDelay +
          pathRenderDelay
      }
    },
    default: {
      totalDuration: 0,
      dotAngle: 0,
      text: t('l.application.view.analyzing_singular.text', 'Analyzing'),
      color: colors.grey72,
      path1: { length: 0, duration: 0, delay: 0 },
      path2: { length: 0, duration: 0, delay: 0 },
      path3: { length: 0, duration: 0, delay: 0 },
      path4: { length: 0, duration: 0, delay: 0 }
    }
  }

  const drawColor1 = {
    hidden: { pathLength: 0, opacity: 0, rotate: 180 },
    visible: {
      pathLength: scoreTier?.[rate]?.path1?.length,
      opacity: 1,
      rotate: 180,
      transition: {
        pathLength: {
          ease: 'linear',
          duration: scoreTier?.[rate]?.path1?.duration,
          bounce: 0,
          delay: scoreTier?.[rate]?.path1?.delay
        },
        opacity: { duration: 0.25, delay: scoreTier?.[rate]?.path1?.delay }
      }
    }
  }
  const drawColor2 = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: scoreTier?.[rate]?.path2?.length,
      opacity: 1,
      rotate: 243,
      transition: {
        pathLength: {
          ease: 'linear',
          duration: scoreTier?.[rate]?.path2?.duration,
          bounce: 0,
          delay: scoreTier?.[rate]?.path2?.delay
        },
        opacity: { duration: 0.25, delay: scoreTier?.[rate]?.path2?.delay }
      }
    }
  }
  const drawColor3 = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: scoreTier?.[rate]?.path3?.length,
      opacity: 1,
      rotate: 284,
      transition: {
        pathLength: {
          ease: 'linear',
          duration: scoreTier?.[rate]?.path3?.duration,
          bounce: 0,
          delay: scoreTier?.[rate]?.path3?.delay
        },
        opacity: { duration: 0.25, delay: scoreTier?.[rate]?.path3?.delay }
      }
    }
  }

  const drawColor4 = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: scoreTier?.[rate]?.path4?.length,
      opacity: 1,
      rotate: 327,
      transition: {
        pathLength: {
          ease: 'linear',
          duration: scoreTier?.[rate]?.path4?.duration,
          bounce: 0,
          delay: scoreTier?.[rate]?.path4?.delay
        },
        opacity: { duration: 0.25, delay: scoreTier?.[rate]?.path4?.delay }
      }
    }
  }

  const getNumberArea = useCallback(() => {
    if (isLoading) return <Skeleton width={70} height={70} />
    if (isLocked) {
      return <LargeLock />
    } else if (isAnalyzing) {
      return <TrustScoreClock />
    } else {
      return (
        <CountUp end={score} duration={scoreTier?.[rate]?.totalDuration} delay={initialDelay} />
      )
    }
  }, [isLoading, isLocked, isAnalyzing, score, rate])

  const getText = useCallback(() => {
    if (isLoading) return <Skeleton width={65} height={20} />
    if (isLocked) {
      return t('l.application.view.locked_score.text', 'Locked')
    } else if (isAnalyzing) {
      return t('l.application.view.analyzing_score.text', 'Analyzing')
    } else {
      return scoreTier?.[rate]?.text
    }
  }, [isLoading, isLocked, isAnalyzing, rate])

  const disabledState = isAnalyzing || isLocked || isLoading

  const { title, content, mobileContent, isPoweredByEquifax } = getModalInfo('trust-score', t)
  const handleInfoClick = () =>
    showInfoModal({
      title,
      content,
      mobileContent,
      isPoweredByEquifax
    })

  return (
    <Container>
      <Bar
        duration={!disabledState ? scoreTier?.[rate]?.totalDuration : 0}
        rotation={`${!disabledState ? scoreTier?.[rate]?.dotAngle : 0}deg`}
        delay={initialDelay}
        isLocked={isLocked}
      >
        <Circle borderColor={!disabledState ? scoreTier?.[rate]?.color : colors.grey72} />
      </Bar>
      <ScoreContainer
        disabledStyling={disabledState}
        textColor={!disabledState ? scoreTier?.[rate]?.color : colors.grey72}
      >
        <ScoreNumber>{getNumberArea()}</ScoreNumber>
        <ScoreTextContainer disabledStyling={disabledState}>
          <ScoreEnd>0</ScoreEnd>
          {getText()}
          <ScoreEnd>100</ScoreEnd>
        </ScoreTextContainer>
        <ScoreInfoContainer id="application-trust-score-info-score-meter" onClick={handleInfoClick}>
          <ClickWrapper>
            <WhatsThisText>{t('l.application.view.whats_this.text', "What's this?")}</WhatsThisText>
            <StyledInfoCircle />
          </ClickWrapper>
        </ScoreInfoContainer>
      </ScoreContainer>
      <motion.svg
        width="200"
        height="160"
        viewBox="0 0 200 160"
        initial="hidden"
        animate="visible"
        rotate="180"
      >
        <ScorePathSkeleton />
        {score >= 0 && !disabledState && (
          <motion.circle
            fill={'rgba(0,0,0,0)'}
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            cx="100"
            cy="100"
            r="90"
            stroke={scoreTier?.[rate]?.color}
            variants={drawColor1}
            custom={1}
          />
        )}
        {score >= averageScoreStart && !disabledState && (
          <motion.circle
            fill={'rgba(0,0,0,0)'}
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            cx="100"
            cy="100"
            r="90"
            stroke={scoreTier?.[rate]?.color}
            variants={drawColor2}
            custom={2}
          />
        )}
        {score >= veryGoodScoreStart && !disabledState && (
          <motion.circle
            fill={'rgba(0,0,0,0)'}
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            cx="100"
            cy="100"
            r="90"
            stroke={scoreTier?.[rate]?.color}
            variants={drawColor3}
            custom={3}
          />
        )}
        {score >= excellentScoreStart && !disabledState && (
          <motion.circle
            fill={'rgba(0,0,0,0)'}
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            cx="100"
            cy="100"
            r="90"
            stroke={scoreTier?.[rate]?.color}
            variants={drawColor4}
            custom={4}
          />
        )}
      </motion.svg>
    </Container>
  )
}

export default AnimatedTrustScore
